// Render-time `open_now` computation — a TypeScript port of the agent-server
// `core/places/open_now.py` `compute_open_now`. The wire (PlacesCard place-item) carries
// only the raw `hours` (+ `tz_id`); the "영업중 / 영업종료 / 곧 마감" badge is a derived
// point-in-time value the FE recomputes at render time. Keeping the
// logic here — not on the wire — means a scrolled-back surface always shows a badge
// correct for *now*, not for when the message was first sent.
//
// Logic parity with the Python golden fixtures (`tests/fixtures/open_now_golden.json`,
// O1) is the contract: past-midnight, closing_soon, 24h, multi-segment gap, hours=null,
// and holiday-branch selection all behave identically (the shared fixture feeds
// `is_holiday` as an input, so it verifies the compute logic, not holiday-list agreement
// between the two libraries — O4-1 only requires logic parity).
import Holidays from 'date-holidays';
import { Temporal } from 'temporal-polyfill';

// `Interval.day`: 0=Mon … 6=Sun (matches Python `date.weekday()`) + 7=holiday hours.
// `start_min`/`end_min` are minutes from the place-local midnight; `end_min` may exceed
// 1440 to encode past-midnight (25:00 → 1500) and 24h (0..1440) in one model.
export interface Interval {
  day: number;
  start_min: number;
  end_min: number;
}

export interface OpenStatus {
  /** null = no hours info (hours === null). */
  openNow: boolean | null;
  closingSoon: boolean | null;
  /** e.g. "08:30~25:00", or null when today has no interval. */
  todayHours: string | null;
}

export interface ComputeOptions {
  /** Does the source provide holiday hours? KR public data = true, Google = false. */
  holidayAware?: boolean;
  countryCode?: string | null;
}

const DAY_MINUTES = 24 * 60; // 1440
// "마감임박" threshold — open now, but closed `CLOSING_SOON_MINUTES` from now → closing soon.
export const CLOSING_SOON_MINUTES = 60;
export const HOLIDAY_DAY = 7;

// Holiday detection via `date-holidays` (the JS counterpart to Python's `holidays`).
// Without this the day=7 holiday-hours branch never fires and a place with
// distinct holiday hours shows its weekday schedule on a public holiday — wrong open
// status exactly on the highest-demand day. A `Holidays` instance is cached per country
// (construction parses that country's whole ruleset). `isoDate` is a place-local
// calendar date (YYYY-MM-DD); it is evaluated at local noon so a timezone offset can't
// roll it to an adjacent day. Only `public` holidays count (not observances/optional).
const _holidaysByCountry = new Map<string, Holidays | null>();

function isHoliday(isoDate: string, countryCode: string | null | undefined): boolean {
  if (!countryCode) return false;
  const cc = countryCode.toUpperCase();
  let hd = _holidaysByCountry.get(cc);
  if (hd === undefined) {
    try {
      hd = new Holidays(cc);
    } catch {
      hd = null; // unknown country → treat as no-holiday (never throw at render)
    }
    _holidaysByCountry.set(cc, hd);
  }
  if (hd === null) return false;
  const result = hd.isHoliday(new Date(`${isoDate}T12:00:00`));
  return Array.isArray(result) && result.some((h) => h.type === 'public');
}

function weekdayMon0(zdt: Temporal.ZonedDateTime): number {
  // Temporal dayOfWeek: 1=Mon … 7=Sun → internal 0=Mon … 6=Sun.
  return zdt.dayOfWeek - 1;
}

function openAt(
  hours: Interval[],
  local: Temporal.ZonedDateTime,
  holidayAware: boolean,
  countryCode: string | null | undefined,
): boolean {
  const minute = local.hour * 60 + local.minute;
  const weekday = weekdayMon0(local);

  let todayIntervals: Interval[];
  let prevIntervals: Interval[];
  if (holidayAware && countryCode != null && isHoliday(local.toPlainDate().toString(), countryCode)) {
    todayIntervals = hours.filter((i) => i.day === HOLIDAY_DAY);
    prevIntervals = []; // holiday hours are self-contained (skip prev-day spillover)
  } else {
    todayIntervals = hours.filter((i) => i.day === weekday);
    const prevDay = (weekday - 1 + 7) % 7;
    prevIntervals = hours.filter((i) => i.day === prevDay && i.end_min > DAY_MINUTES);
  }

  // Today's intervals: minute < 1440, so end_min > 1440 (past-midnight/24h) always holds.
  for (const iv of todayIntervals) {
    if (iv.start_min <= minute && minute < iv.end_min) return true;
  }
  // Previous day's past-midnight interval spilling into today's small hours.
  for (const iv of prevIntervals) {
    if (minute < iv.end_min - DAY_MINUTES) return true;
  }
  return false;
}

function minToHhmm(m: number): string {
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

function formatToday(intervals: Interval[]): string | null {
  if (intervals.length === 0) return null;
  return [...intervals]
    .sort((a, b) => a.start_min - b.start_min)
    .map((iv) => `${minToHhmm(iv.start_min)}~${minToHhmm(iv.end_min)}`)
    .join(', ');
}

/**
 * Place-local open status at `now`. `now` is an instant (JS `Date`); it is converted
 * to the place timezone (`tzId`) before evaluation, exactly like the Python
 * `now.astimezone(ZoneInfo(tz_id))`.
 */
export function computeOpenNow(
  hours: Interval[] | null | undefined,
  tzId: string,
  now: Date,
  options: ComputeOptions = {},
): OpenStatus {
  // hours 부재 또는 tz_id 빈값 → 판정 불가(Python `hours is None or not tz_id`, S1).
  if (hours == null || !tzId) return { openNow: null, closingSoon: null, todayHours: null };

  const { holidayAware = true, countryCode = null } = options;
  const local = Temporal.Instant.fromEpochMilliseconds(now.getTime()).toZonedDateTimeISO(tzId);
  const holidayAwareHere = holidayAware && countryCode != null;

  const openNow = openAt(hours, local, holidayAwareHere, countryCode);
  // closing_soon = open now AND not open at now+threshold. Evaluating at now+threshold
  // makes 24h / continuous past-midnight spans naturally read as "not closing" (no need
  // to merge intervals).
  const soonLocal = local.add({ minutes: CLOSING_SOON_MINUTES });
  const closingSoon = openNow && !openAt(hours, soonLocal, holidayAwareHere, countryCode);

  const todayIsHoliday =
    holidayAwareHere && isHoliday(local.toPlainDate().toString(), countryCode);
  const todayIntervals = todayIsHoliday
    ? hours.filter((i) => i.day === HOLIDAY_DAY)
    : hours.filter((i) => i.day === weekdayMon0(local));

  return { openNow, closingSoon, todayHours: formatToday(todayIntervals) };
}
